import React, { useState } from 'react';
import { Box, ContentTemplate, Progress } from 'web-library';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { joiResolver } from '@hookform/resolvers/joi';
import {
  UserGroupAccessConfigurationResponse,
  UserGroupDetail,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { RequestProcessRequest, TaskDetailsResponse } from 'web-library/models/Request';
import TaskStatusUpdate from 'web-library/components/TaskStatusUpdate';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import UserGroupAccessTaskDetails from 'web-library/components/UserGroupAccessTaskDetails';
import { updateTaskStatusSchema } from 'web-library/models/TaskOrRequest/UpdateTaskStatusSchema';
import { useGetRequestOrTaskDetails } from '../../../../api-refactored/task/useGetTaskOrRequestDetails';
import UserGroupTaskDetails from './UserGroupTaskDetails';
import { useGetDownloadRequestFilePresignedUrl } from '../../../../api-refactored/task/useGetDownloadRequestFilePresignedUrl';
import { usePostProcessRequest } from '../../../../api-refactored/user/usePostProcessRequest';
import RouteKey from '../../../../routes/key';
import FunctionAccessDetails from '../FunctionAccessDetails';
import AccountManagementDetails from '../AccountManagementDetails';
import DataArchivalDetailComponent from '../DataArchival/components/DataArchivalDetailComponent';
import SystemSettingsDetails from '../SystemSettingsDetails';

const useStyles = makeStyles()((theme) =>
({
  divider: {
    paddingBottom: theme.spacing(4),
  },
}));

const ViewTaskDetails = () => {
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const { uuid } = useParams<{ uuid: string }>();
  const { classes } = useStyles();
  const history = useHistory();
  const formHook = useForm({
    mode: 'onSubmit',
    resolver: joiResolver(updateTaskStatusSchema),
  });

  const [taskDetails, setTaskDetails] = useState<TaskDetailsResponse>();
  const [fileUuid, setFileUuid] = useState<string>('');

  const { admin } = RouteKey.child;
  const { tasklist, requestlist } = RouteKey.admin.child;
  const { getValues } = formHook;
  const isApprover = path.includes('my-tasks');

  useGetRequestOrTaskDetails({
    onSuccess: (res) => {
      setTaskDetails(res);
    },
    uuid,
  });

  const { mutate: mutatePostProcessRequest, status: postProcessRequestMutationStatus } =
    usePostProcessRequest({
      onSuccess: () => {
        handleBackToTaskRequestList({
          requestStatus: getValues('status'),
          selectedTab: 1,
        });
      },
    });

  useGetDownloadRequestFilePresignedUrl({
    fileUuid: fileUuid || '',
    enabled: !!fileUuid && fileUuid !== '',
    onSuccess: (res) => {
      if (res.url) {
        const newWindow = window.open(res.url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
          newWindow.opener = null;
        }
        setFileUuid('');
      }
    },
  });

  const handleBackToTaskRequestList = (state?: unknown) => {
    const requestStatus = taskDetails?.requestStatus === 'Pending' ? 'Pending' : 'Archived';

    if (isApprover) {
      history.push(`/${admin}/${tasklist.route}?requestStatus=${requestStatus}`, state);
    } else {
      history.push(`/${admin}/${requestlist.route}?requestStatus=${requestStatus}`, state);
    }
  };

  const processRequest = () => {
    const requestProcessRequest: RequestProcessRequest = {
      requestUuid: uuid,
      status: getValues('status').toUpperCase(),
      remarks: getValues('remarks'),
    };

    mutatePostProcessRequest(requestProcessRequest);
  };

  const getInstitutionNameForFunctionAccessDetails = () => {
    if (!taskDetails?.content?.institutions) {
      return '';
    }

    const [institutionName] =
      Object.entries(taskDetails.content.institutions).find(
        ([_, institutionUuid]) => institutionUuid === taskDetails?.sourceUuid
      ) || [];

    return institutionName || '';
  };

  if (!taskDetails) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  const institutionNameForFunctionAccessDetails = getInstitutionNameForFunctionAccessDetails();

  return (
    <>
      {taskDetails?.type === 'USER_GROUP' && (
        <UserGroupTaskDetails userGroupDetail={taskDetails?.content as UserGroupDetail} />
      )}

      {taskDetails?.type === 'ADMIN_ACCESS_CONFIG' && (
        <UserGroupAccessTaskDetails
          type={taskDetails?.type}
          userGroupAccessConfiguration={
            taskDetails?.content as UserGroupAccessConfigurationResponse
          }
        />
      )}

      {taskDetails?.type === 'SYSTEM_SETTINGS' && (
        <ContentTemplate isContinuable>
          <SystemSettingsDetails systemSettingsData={taskDetails?.content} />
        </ContentTemplate>
      )}

      {taskDetails?.type === 'ACCOUNT_MANAGEMENT' && (
        <AccountManagementDetails taskDetails={taskDetails} />
      )}

      {(taskDetails?.type === 'CANDIDATE_ACCESS_CONFIG' ||
        taskDetails?.type === 'COORDINATOR_ACCESS_CONFIG') && (
          <FunctionAccessDetails
            accessConfigurationListingData={taskDetails?.content}
            selectedInstitution={{
              key: institutionNameForFunctionAccessDetails,
              value: institutionNameForFunctionAccessDetails,
            }}
            targetRole={
              taskDetails?.type === 'CANDIDATE_ACCESS_CONFIG'
                ? t('admin->>UserGroups.Candidate')
                : t('admin->>UserGroups.Coordinator')
            }
          />
        )}

      {taskDetails?.type === 'DATA_ARCHIVAL' && (
        <DataArchivalDetailComponent archivalDetail={taskDetails} />
      )}

      {taskDetails?.type !== 'ACCOUNT_MANAGEMENT' && (
        <ContentTemplate isContinuable continuous isDividerBottom>
          <Box className={classes.divider} />
        </ContentTemplate>
      )}

      <TaskStatusUpdate
        taskDetails={taskDetails}
        formHook={formHook}
        handleBackToListing={() => handleBackToTaskRequestList()}
        processRequest={processRequest}
        processRequestStatus={postProcessRequestMutationStatus}
        isApprover={isApprover}
        setFileUuid={setFileUuid}
      />
    </>
  );
};
export default ViewTaskDetails;
